import { useEffect, useState, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import {
    PawPrint, Phone, WhatsappLogo, MapPin, Pill, FirstAid, HeartStraight, Gift, HandHeart,
    CheckCircle, WarningCircle, NavigationArrow, Siren, ShieldWarning, FileText, Certificate,
    Airplane, Shield, ArrowSquareOut, House, SpeakerHigh, ChatCircleText, Microphone,
} from "@phosphor-icons/react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { track } from "@/lib/analytics";
import AudioRecorder from "@/components/AudioRecorder";
import { SUPPORT_EMAIL, supportMailto } from "@/constants/support";

const HERO_IMG = null; // legacy — no longer used

/* ---------- Personality shared library ---------- */
const PERSONALITY_MAP = {
    friendly_people: { emoji: "🐶", tone: "emerald", labelKey: "safety.p.friendly_people" },
    friendly_dogs: { emoji: "🐕", tone: "emerald", labelKey: "safety.p.friendly_dogs" },
    friendly_cats: { emoji: "🐈", tone: "emerald", labelKey: "safety.p.friendly_cats" },
    good_children: { emoji: "👶", tone: "emerald", labelKey: "safety.p.good_children" },
    trained: { emoji: "🎓", tone: "emerald", labelKey: "safety.p.trained" },
    energetic: { emoji: "🎾", tone: "emerald", labelKey: "safety.p.energetic" },
    nervous: { emoji: "😨", tone: "amber", labelKey: "safety.p.nervous" },
    needs_space: { emoji: "🚫", tone: "amber", labelKey: "safety.p.needs_space" },
    do_not_approach: { emoji: "⚠️", tone: "amber", labelKey: "safety.p.do_not_approach" },
    medical_needs: { emoji: "💊", tone: "sky", labelKey: "safety.p.medical_needs" },
    special_diet: { emoji: "🥣", tone: "sky", labelKey: "safety.p.special_diet" },
    needs_medication: { emoji: "💉", tone: "sky", labelKey: "safety.p.needs_medication" },
};
const TONE_CLASS = {
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/25",
    amber: "bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/25",
    sky: "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/25",
};

const DOC_KIND_META = {
    vaccine: { emoji: "💉", labelKey: "safety.dockind.vaccine", tone: "from-rose-500 to-pink-500", icon: <Pill size={16} weight="fill" /> },
    rabies: { emoji: "🦠", labelKey: "safety.dockind.rabies", tone: "from-red-500 to-orange-500", icon: <FirstAid size={16} weight="fill" /> },
    vet: { emoji: "🩺", labelKey: "safety.dockind.vet", tone: "from-emerald-500 to-teal-500", icon: <Certificate size={16} weight="fill" /> },
    medical: { emoji: "❤️‍🩹", labelKey: "safety.dockind.medical", tone: "from-pink-500 to-fuchsia-500", icon: <Pill size={16} weight="fill" /> },
    insurance: { emoji: "🛡️", labelKey: "safety.dockind.insurance", tone: "from-sky-500 to-blue-500", icon: <Shield size={16} weight="fill" /> },
    travel: { emoji: "✈️", labelKey: "safety.dockind.travel", tone: "from-indigo-500 to-violet-500", icon: <Airplane size={16} weight="fill" /> },
    other: { emoji: "📄", labelKey: "safety.dockind.other", tone: "from-slate-500 to-zinc-500", icon: <FileText size={16} weight="fill" /> },
};

export default function Finder() {
    const { publicId } = useParams();
    const { t } = useI18n();
    const [pet, setPet] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [foundOpen, setFoundOpen] = useState(false);
    const [reported, setReported] = useState(false);
    const scanReported = useRef(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/public/pets/${publicId}`);
                setPet(data);
                // GA4: track that the finder page was actually rendered with a
                // real pet (not 404). Lost vs standard is a useful dimension.
                track("finder_view", {
                    public_id: publicId,
                    is_lost: !!data?.is_lost,
                });
            } catch (e) {
                setError(formatApiError(e.response?.data?.detail) || "Pet not found");
            } finally { setLoading(false); }
        })();
    }, [publicId]);

    useEffect(() => {
        if (!pet || scanReported.current) return;
        const send = (body) => {
            if (scanReported.current) return;
            scanReported.current = true;
            api.post(`/public/pets/${publicId}/scan`, body).catch(() => { });
        };
        if (typeof navigator === "undefined" || !navigator.geolocation) {
            send({ location_source: "ip" });
            return;
        }
        // Strategy: request a high-accuracy fix. If the first fix is coarse
        // (accuracy > 100m), keep watching for up to ~14s to let the GPS lock,
        // then report the best (smallest accuracy) fix we captured. This
        // gives the owner the tightest possible pin on Google Maps.
        let best = null;
        let watchId = null;
        const clearWatch = () => {
            try { if (watchId != null) navigator.geolocation.clearWatch(watchId); } catch (_) { /* ignore */ }
            watchId = null;
        };
        const finish = (payload) => {
            clearWatch();
            send(payload);
        };
        watchId = navigator.geolocation.watchPosition(
            (pos) => {
                const acc = pos.coords.accuracy;
                if (!best || acc < best.accuracy) {
                    best = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: acc };
                }
                // Great fix — send immediately.
                if (acc <= 25) finish({ ...best, location_source: "gps" });
            },
            () => finish({ location_source: "ip" }),
            { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
        // Hard stop after 14s — use best-so-far or fall back to IP.
        const stopTimer = setTimeout(() => {
            if (best) finish({ ...best, location_source: "gps" });
            else finish({ location_source: "ip" });
        }, 14000);
        return () => { clearTimeout(stopTimer); clearWatch(); };
    }, [pet, publicId]);

    if (loading) return (
        <div className="min-h-screen grid place-items-center bg-paw-bone dark:bg-[hsl(152,15%,8%)] text-paw-ink2 dark:text-paw-bone/60" data-testid="finder-loading">
            {t("common.loading")}
        </div>
    );

    if (error || !pet) return (
        <div className="min-h-screen grid place-items-center bg-paw-bone dark:bg-[hsl(152,15%,8%)] px-6" data-testid="finder-error">
            <div className="card-soft p-8 max-w-md text-center">
                <WarningCircle size={40} weight="duotone" className="mx-auto text-paw-terra" />
                <h1 className="mt-4 font-display text-2xl text-paw-ink dark:text-paw-bone">{t("finder.not_found_h")}</h1>
                <p className="mt-2 text-paw-ink2 dark:text-paw-bone/60">{t("finder.not_found_sub")}</p>
                <Link to="/" className="mt-6 inline-flex btn-outline">{t("finder.go_home")}</Link>
            </div>
        </div>
    );

    const isLost = Boolean(pet.is_lost);

    return isLost
        ? <LostFinder pet={pet} publicId={publicId} foundOpen={foundOpen} setFoundOpen={setFoundOpen} reported={reported} setReported={setReported} />
        : <StandardFinder pet={pet} publicId={publicId} foundOpen={foundOpen} setFoundOpen={setFoundOpen} reported={reported} setReported={setReported} />;
}

/* ============================================================
   LOST — EMERGENCY LAYOUT
   ============================================================ */
function LostFinder({ pet, publicId, foundOpen, setFoundOpen, reported, setReported }) {
    const { t } = useI18n();
    const phone = pet.owner_phone;
    const whatsapp = pet.owner_whatsapp; // optional secondary
    const missingSince = pet.lost_since ? new Date(pet.lost_since) : null;
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const id = setInterval(() => setNow(Date.now()), 30000); return () => clearInterval(id); }, []);
    const elapsed = missingSince ? formatElapsed(now - missingSince.getTime(), t) : "";

    return (
        <div className="min-h-screen bg-red-50 dark:bg-red-950/40 text-paw-ink dark:text-paw-bone" data-testid="finder-lost">
            {/* Emergency banner */}
            <div className="bg-gradient-to-b from-red-600 to-red-700 text-white" data-testid="finder-lost-banner">
                <div className="max-w-3xl mx-auto px-5 sm:px-6 py-4 flex items-center gap-2.5">
                    <div className="relative">
                        <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                        <span className="relative block w-2.5 h-2.5 rounded-full bg-white" />
                    </div>
                    <p className="text-[11px] sm:text-xs uppercase tracking-[0.25em] font-bold">{t("finder.lost.badge")}</p>
                    <span className="ms-auto text-[11px] text-white/80">{t("finder.lost.pawlink_verified")}</span>
                </div>
            </div>

            {/* Hero */}
            <div className="max-w-3xl mx-auto px-5 sm:px-6 pt-6">
                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-red-600 to-red-800 text-white shadow-hover" data-testid="finder-lost-hero">
                    <div aria-hidden className="absolute -top-24 -end-16 w-72 h-72 rounded-full bg-yellow-300/15 blur-3xl" />
                    <div aria-hidden className="absolute -bottom-24 -start-16 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
                    <div className="relative p-6 sm:p-8 text-center">
                        <div className="mx-auto w-36 h-36 sm:w-40 sm:h-40 rounded-full ring-4 ring-white/40 shadow-hover overflow-hidden bg-white/10 grid place-items-center" data-testid="finder-lost-photo">
                            {pet.photo_path
                                ? <img src={fileUrl(pet.photo_path)} alt={pet.name} className="pet-photo w-full h-full object-cover" />
                                : <PawPrint size={64} weight="duotone" />}
                        </div>
                        <p className="mt-5 text-[11px] uppercase tracking-[0.2em] font-bold text-yellow-200/95">{t("finder.lost.headline_kicker")}</p>
                        <h1 className="mt-2 font-display text-4xl sm:text-5xl tracking-tight" data-testid="finder-lost-name">{pet.name}</h1>
                        <p className="mt-2 text-white/90 text-base sm:text-lg font-medium">
                            {t("finder.lost.headline", { name: pet.name })}
                        </p>
                        {elapsed && (
                            <p className="mt-1 text-sm text-white/70" data-testid="finder-lost-elapsed">
                                {t("finder.lost.missing_for", { time: elapsed })}
                            </p>
                        )}

                        {/* Massive call button */}
                        {phone && (
                            <>
                                <a href={`tel:${phone}`}
                                    data-testid="finder-lost-call"
                                    className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white text-red-700 font-bold px-6 py-5 text-lg hover:bg-white/95 transition shadow-hover">
                                    <Phone size={22} weight="fill" /> {t("finder.lost.call_cta")}
                                </a>
                                <a href={`sms:${phone}?body=${encodeURIComponent(`Hi! I think I found ${pet.name}. `)}`}
                                    data-testid="finder-lost-sms"
                                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white/15 backdrop-blur border border-white/25 text-white font-semibold px-5 py-4 text-base hover:bg-white/25 transition">
                                    <ChatCircleText size={20} weight="fill" /> {t("finder.lost.sms_cta")}
                                </a>
                            </>
                        )}
                        {whatsapp && (
                            <a href={`https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi! I think I found ${pet.name}. `)}`}
                                target="_blank" rel="noreferrer"
                                data-testid="finder-lost-whatsapp"
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-white/10 backdrop-blur border border-white/20 text-white/85 font-medium px-5 py-3 text-sm hover:bg-white/20 transition">
                                <WhatsappLogo size={16} weight="fill" /> {t("finder.lost.whatsapp_cta")}
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Emotional plea */}
            <main className="max-w-3xl mx-auto px-5 sm:px-6 py-6 space-y-4" data-testid="finder-lost-body">
                <section className="rounded-3xl bg-white dark:bg-white/5 border border-red-200 dark:border-red-800/40 p-5 sm:p-6" data-testid="finder-lost-plea">
                    <div className="flex items-start gap-3">
                        <HeartStraight size={22} weight="fill" className="text-red-600 flex-shrink-0 mt-1" />
                        <div>
                            <h2 className="font-display text-xl text-paw-ink dark:text-paw-bone">{t("finder.lost.plea_h", { name: pet.name })}</h2>
                            <p className="mt-1.5 text-sm text-paw-ink2 dark:text-paw-bone/70 leading-relaxed whitespace-pre-line">
                                {pet.lost_instructions || t("finder.lost.plea_default", { name: pet.name })}
                            </p>
                        </div>
                    </div>
                </section>

                {/* Last seen */}
                {(pet.lost_last_seen_location || pet.lost_last_seen_latitude) && (
                    <section className="card-soft p-5 sm:p-6" data-testid="finder-lost-lastseen">
                        <div className="flex items-center gap-2 mb-2 text-red-600">
                            <MapPin size={20} weight="fill" />
                            <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("finder.lost.lastseen_h")}</h2>
                        </div>
                        {pet.lost_last_seen_location && <p className="text-paw-ink dark:text-paw-bone">{pet.lost_last_seen_location}</p>}
                        {pet.lost_last_seen_latitude && pet.lost_last_seen_longitude && (
                            <a href={`https://www.google.com/maps?q=${pet.lost_last_seen_latitude},${pet.lost_last_seen_longitude}`}
                                target="_blank" rel="noreferrer"
                                data-testid="finder-lost-map"
                                className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-paw-forest dark:text-paw-terra hover:underline">
                                <NavigationArrow size={14} weight="fill" /> {t("finder.lost.view_on_map")}
                            </a>
                        )}
                    </section>
                )}

                {/* Personality */}
                <PersonalityStrip pet={pet} />

                {/* Owner voice */}
                {pet.audio_path && (
                    <section className="card-soft p-5 sm:p-6" data-testid="finder-audio">
                        <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone mb-2">{t("finder.voice_h")}</h2>
                        <p className="text-sm text-paw-ink2 dark:text-paw-bone/60 mb-3">{t("finder.voice_sub")}</p>
                        <audio src={fileUrl(pet.audio_path)} controls className="w-full" data-testid="finder-audio-player" />
                    </section>
                )}

                {/* Medical / allergies */}
                {(pet.allergies || pet.medical_notes) && (
                    <section className="card-soft p-5 sm:p-6" data-testid="finder-medical-block">
                        <div className="flex items-center gap-2 mb-2 text-red-600">
                            <FirstAid size={20} weight="fill" />
                            <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("finder.lost.medical_h")}</h2>
                        </div>
                        {pet.allergies && <p className="text-sm text-paw-ink dark:text-paw-bone whitespace-pre-line"><b>{t("finder.allergies_h")}:</b> {pet.allergies}</p>}
                        {pet.medical_notes && <p className="mt-1.5 text-sm text-paw-ink dark:text-paw-bone whitespace-pre-line"><b>{t("finder.medical_h")}:</b> {pet.medical_notes}</p>}
                    </section>
                )}

                {/* Public passport documents */}
                <PassportPublicSection pet={pet} />

                {/* I saw this pet — one-shot GPS sighting */}
                <SightingShortcut publicId={publicId} petName={pet.name} />

                {/* Standalone finder → owner voice message shortcut */}
                <VoiceMessageShortcut publicId={publicId} petName={pet.name} />

                {/* I found them */}
                <FoundSection pet={pet} reported={reported} openModal={() => setFoundOpen(true)} />

                <p className="text-center text-xs text-paw-ink2/70 dark:text-paw-bone/40 pt-4">
                    {t("finder.powered")} <Link to="/" className="text-paw-forest dark:text-paw-terra font-semibold">PawLink</Link> · {t("finder.tagline")}
                </p>
                <FinderSupportNote t={t} />
            </main>

            {foundOpen && (
                <FoundModal publicId={publicId}
                    onClose={() => setFoundOpen(false)}
                    onSuccess={() => { setReported(true); setFoundOpen(false); }} />
            )}
        </div>
    );
}

/* ============================================================
   STANDARD FINDER — pet is NOT lost (premium public profile)
   ============================================================ */
function StandardFinder({ pet, publicId, foundOpen, setFoundOpen, reported, setReported }) {
    const { t } = useI18n();
    const phone = pet.owner_phone;
    const whatsapp = pet.owner_whatsapp; // only shown if owner explicitly set it
    const hasContact = Boolean(phone);
    const hasMedical = pet.allergies || pet.medical_notes;
    const locationLine = [pet.city, pet.region].filter(Boolean).join(", ");
    return (
        <div className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,8%)] text-paw-ink dark:text-paw-bone">
            {/* =====================================================================
                DESKTOP (lg+) — premium two-column card, max-w-[1200px], centered.
                Mobile layout below is untouched.
               ===================================================================== */}
            <section
                data-testid="finder-desktop"
                className="hidden lg:block max-w-[1200px] mx-auto px-6 pt-16 pb-10"
            >
                <div className="grid lg:grid-cols-2 gap-10 items-stretch">
                    {/* LEFT — big pet photo 4:3, rounded, cover */}
                    <div className="relative rounded-[28px] overflow-hidden bg-paw-muted dark:bg-white/5 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)]">
                        <div className="aspect-[4/3] w-full h-full">
                            {pet.photo_path ? (
                                <img
                                    src={fileUrl(pet.photo_path)}
                                    alt={pet.name}
                                    className="pet-photo w-full h-full object-cover"
                                    data-testid="finder-desktop-photo"
                                />
                            ) : (
                                <div className="w-full h-full grid place-items-center text-paw-forest/40 dark:text-paw-terra/40">
                                    <PawPrint size={128} weight="duotone" />
                                </div>
                            )}
                        </div>
                        <div aria-hidden className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/50 to-transparent" />
                        <div className="absolute top-5 start-5 inline-flex items-center gap-1.5 rounded-full bg-white/95 dark:bg-paw-ink/90 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold text-paw-forest dark:text-paw-terra shadow-md">
                            <HeartStraight size={11} weight="fill" /> {t("finder.im_lost")}
                        </div>
                    </div>

                    {/* RIGHT — identity, contact, medical, reward — same height as photo via items-stretch */}
                    <div className="flex flex-col justify-between rounded-[28px] border border-paw-line dark:border-white/10 bg-white dark:bg-[hsl(152,12%,11%)] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] p-8 xl:p-10">
                        {/* Identity */}
                        <div>
                            {pet.owner_name && (
                                <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra">
                                    {t("finder.owner")} · <span className="text-paw-ink dark:text-paw-bone normal-case tracking-normal">{pet.owner_name}</span>
                                </p>
                            )}
                            <h1 className="mt-2 font-display text-5xl xl:text-6xl leading-none tracking-tight text-paw-ink dark:text-paw-bone" data-testid="finder-desktop-name">
                                {pet.name}
                            </h1>
                            <p className="mt-2 text-base text-paw-ink2 dark:text-paw-bone/70 capitalize">
                                {t(`petform.type_${pet.type}`)}{locationLine ? ` · ${locationLine}` : ""}
                            </p>

                            <p className="mt-5 text-lg text-paw-ink dark:text-paw-bone leading-relaxed">
                                &ldquo;{pet.finder_message || t("finder.default_plea", { name: pet.name })}&rdquo;
                            </p>
                        </div>

                        {/* Contact + medical block */}
                        <div className="mt-8 space-y-4">
                            {hasContact && (
                                <div className="grid grid-cols-2 gap-3" data-testid="finder-desktop-contact">
                                    <a data-testid="finder-desktop-call" href={`tel:${phone}`}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white font-semibold text-base py-4 shadow-[0_14px_40px_-8px_rgba(248,122,42,0.5)] transition-all duration-300 hover:-translate-y-0.5">
                                        <Phone size={20} weight="fill" />{t("finder.call")}
                                    </a>
                                    <a data-testid="finder-desktop-sms" href={`sms:${phone}?body=${encodeURIComponent(`Hi! I think I found ${pet.name}. `)}`}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-white/10 dark:hover:bg-white/15 text-white font-semibold text-base py-4 shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                                        <ChatCircleText size={20} weight="fill" />{t("finder.text")}
                                    </a>
                                </div>
                            )}
                            {whatsapp && (
                                <a data-testid="finder-desktop-whatsapp"
                                    href={`https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi! I found ${pet.name}. `)}`}
                                    target="_blank" rel="noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-semibold py-3 transition">
                                    <WhatsappLogo size={18} weight="fill" />{t("finder.whatsapp_optional")}
                                </a>
                            )}

                            {(pet.allergies || pet.medical_notes) && (
                                <div className="mt-5 rounded-2xl border border-paw-line dark:border-white/10 overflow-hidden divide-y divide-paw-line dark:divide-white/10">
                                    {pet.allergies && (
                                        <div className="flex items-start gap-3 p-4">
                                            <div className="w-9 h-9 rounded-xl bg-rose-500/10 text-rose-500 grid place-items-center flex-shrink-0"><Pill size={16} weight="fill" /></div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-paw-bone/60">{t("finder.allergies_h")}</p>
                                                <p className="text-sm text-paw-ink dark:text-paw-bone mt-0.5 break-words">{pet.allergies}</p>
                                            </div>
                                        </div>
                                    )}
                                    {pet.medical_notes && (
                                        <div className="flex items-start gap-3 p-4">
                                            <div className="w-9 h-9 rounded-xl bg-red-500/10 text-red-500 grid place-items-center flex-shrink-0"><FirstAid size={16} weight="fill" /></div>
                                            <div className="min-w-0">
                                                <p className="text-[10px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-paw-bone/60">{t("finder.medical_h")}</p>
                                                <p className="text-sm text-paw-ink dark:text-paw-bone mt-0.5 break-words">{pet.medical_notes}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Immersive hero photo (mobile + tablet only — UNTOUCHED) */}
            <section className="relative lg:hidden" data-testid="finder-hero">
                <div className="relative aspect-[4/5] sm:aspect-[16/10] max-h-[560px] overflow-hidden bg-paw-muted dark:bg-white/5">
                    {pet.photo_path ? (
                        <img
                            src={fileUrl(pet.photo_path)}
                            alt={pet.name}
                            className="pet-photo absolute inset-0 w-full h-full object-cover"
                            data-testid="finder-photo"
                        />
                    ) : (
                        <div className="absolute inset-0 grid place-items-center text-paw-forest/40 dark:text-paw-terra/40">
                            <PawPrint size={112} weight="duotone" />
                        </div>
                    )}
                    {/* Gradient overlay for legibility */}
                    <div aria-hidden className="absolute inset-0 bg-gradient-to-b from-transparent via-black/10 to-black/75" />

                    {/* Top-left verified chip */}
                    <div className="absolute top-4 start-4 inline-flex items-center gap-1.5 rounded-full bg-white/90 dark:bg-paw-ink/85 backdrop-blur px-3 py-1 text-[10px] uppercase tracking-widest font-bold text-paw-forest dark:text-paw-terra shadow-soft">
                        <HeartStraight size={11} weight="fill" /> {t("finder.im_lost")}
                    </div>

                    {/* Overlaid identity */}
                    <div className="absolute bottom-0 inset-x-0 p-5 sm:p-6 text-white">
                        <h1 className="font-display text-4xl sm:text-5xl tracking-tight drop-shadow-md" data-testid="finder-name">{pet.name}</h1>
                        <p className="mt-1 text-sm sm:text-base text-white/90 capitalize drop-shadow">
                            {t(`petform.type_${pet.type}`)}{locationLine ? ` · ${locationLine}` : ""}
                        </p>
                    </div>
                </div>
            </section>

            {/* Contact + owner intro (floating card overlapping hero) — mobile only */}
            <div className="relative -mt-10 sm:-mt-14 z-10 lg:hidden">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <section className="rounded-[28px] border border-paw-line dark:border-white/10 bg-white/98 dark:bg-[hsl(152,12%,11%)]/98 backdrop-blur-xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] p-6 sm:p-8" data-testid="finder-contact-card">
                        {pet.owner_name && (
                            <p className="text-center text-[11px] uppercase tracking-[0.22em] font-semibold text-paw-forest dark:text-paw-terra">
                                {t("finder.owner")} · <span className="text-paw-ink dark:text-paw-bone">{pet.owner_name}</span>
                            </p>
                        )}
                        <p className="mt-3 text-base sm:text-lg text-paw-ink dark:text-paw-bone leading-relaxed text-center max-w-xl mx-auto">
                            &ldquo;{pet.finder_message || t("finder.default_plea", { name: pet.name })}&rdquo;
                        </p>
                        {hasContact && (
                            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="finder-contact-actions">
                                <a data-testid="finder-call-btn" href={`tel:${phone}`}
                                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-paw-terra hover:bg-paw-terraDark text-white font-semibold text-base py-4 shadow-[0_14px_40px_-8px_rgba(248,122,42,0.5)] transition-all duration-300 hover:-translate-y-0.5">
                                    <Phone size={20} weight="fill" />
                                    {t("finder.call")}
                                </a>
                                <a data-testid="finder-sms-btn"
                                    href={`sms:${phone}?body=${encodeURIComponent(`Hi! I think I found ${pet.name}. `)}`}
                                    className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-white/10 dark:hover:bg-white/15 text-white font-semibold text-base py-4 shadow-lg transition-all duration-300 hover:-translate-y-0.5">
                                    <ChatCircleText size={20} weight="fill" />
                                    {t("finder.text")}
                                </a>
                            </div>
                        )}
                        {whatsapp && (
                            <a data-testid="finder-whatsapp-btn"
                                href={`https://wa.me/${whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(`Hi! I found ${pet.name}. `)}`}
                                target="_blank" rel="noreferrer"
                                className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 font-semibold text-sm py-3 transition">
                                <WhatsappLogo size={18} weight="fill" />
                                {t("finder.whatsapp_optional")}
                            </a>
                        )}
                    </section>
                </div>
            </div>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="finder-details">
                {/* Owner voice message — premium player */}
                {pet.audio_path && (
                    <OwnerVoiceCard pet={pet} />
                )}

                <PersonalityStrip pet={pet} />

                {/* Consolidated info card: allergies + medical + location — mobile only */}
                {(hasMedical || locationLine) && (
                    <section className="rounded-3xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden lg:hidden" data-testid="finder-info-card">
                        {pet.allergies && (
                            <InfoRow
                                icon={<Pill size={18} weight="fill" />}
                                accent="text-rose-500 bg-rose-500/10"
                                title={t("finder.allergies_h")}
                                body={pet.allergies}
                                testId="finder-allergies"
                            />
                        )}
                        {pet.medical_notes && (
                            <InfoRow
                                icon={<FirstAid size={18} weight="fill" />}
                                accent="text-red-500 bg-red-500/10"
                                title={t("finder.medical_h")}
                                body={pet.medical_notes}
                                testId="finder-medical"
                            />
                        )}
                        {locationLine && (
                            <InfoRow
                                icon={<MapPin size={18} weight="fill" />}
                                accent="text-emerald-600 bg-emerald-500/10"
                                title={t("finder.location_h")}
                                body={locationLine}
                                testId="finder-location"
                                last
                            />
                        )}
                    </section>
                )}

                <PassportPublicSection pet={pet} />

                {/* Standalone finder → owner voice message shortcut */}
                <VoiceMessageShortcut publicId={publicId} petName={pet.name} />

                <FoundSection pet={pet} reported={reported} openModal={() => setFoundOpen(true)} />

                <p className="text-center text-xs text-paw-ink2/70 dark:text-paw-bone/40 pt-4">
                    {t("finder.powered")} <Link to="/" className="text-paw-forest dark:text-paw-terra font-semibold">PawLink</Link> · {t("finder.tagline")}
                </p>
                <FinderSupportNote t={t} />
            </main>

            {foundOpen && (
                <FoundModal publicId={publicId}
                    onClose={() => setFoundOpen(false)}
                    onSuccess={() => { setReported(true); setFoundOpen(false); }} />
            )}
        </div>
    );
}

/* ---------- Premium owner-voice player ---------- */
function OwnerVoiceCard({ pet }) {
    const { t } = useI18n();
    const audioSrc = fileUrl(pet.audio_path);
    return (
        <section
            className="relative overflow-hidden rounded-3xl border border-paw-forest/15 dark:border-paw-terra/20 bg-gradient-to-br from-paw-forest/5 to-paw-terra/8 dark:from-paw-forest/15 dark:to-paw-terra/12 p-5 sm:p-6"
            data-testid="finder-audio"
        >
            <div aria-hidden className="absolute -top-16 -end-16 w-48 h-48 rounded-full bg-paw-terra/15 blur-3xl" />
            <div className="relative flex items-center gap-3 mb-3">
                <div className="w-11 h-11 rounded-2xl bg-paw-forest text-white dark:bg-paw-terra grid place-items-center flex-shrink-0 shadow-hover">
                    <SpeakerHigh size={20} weight="fill" />
                </div>
                <div>
                    <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("finder.voice_h")}</h2>
                    <p className="text-xs text-paw-ink2 dark:text-paw-bone/60">{t("finder.voice_sub")}</p>
                </div>
            </div>
            <audio
                src={audioSrc}
                controls
                preload="metadata"
                className="relative w-full"
                data-testid="finder-audio-player"
            >
                {t("finder.voice_unsupported")}
            </audio>
            <a
                href={audioSrc}
                target="_blank"
                rel="noreferrer"
                data-testid="finder-audio-fallback"
                className="relative mt-2 inline-flex items-center gap-1 text-[11px] text-paw-forest dark:text-paw-terra hover:underline"
            >
                {t("finder.voice_open_new")} ↗
            </a>
        </section>
    );
}

/* ---------- Info row (used inside consolidated info card) ---------- */
function InfoRow({ icon, accent, title, body, testId, last }) {
    return (
        <div className={`flex items-start gap-3 p-4 sm:p-5 ${last ? "" : "border-b border-paw-line/70 dark:border-white/8"}`} data-testid={testId}>
            <div className={`w-9 h-9 rounded-xl grid place-items-center flex-shrink-0 ${accent}`}>{icon}</div>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-widest font-bold text-paw-ink2 dark:text-paw-bone/60">{title}</p>
                <p className="mt-1 text-sm text-paw-ink dark:text-paw-bone leading-relaxed whitespace-pre-line">{body}</p>
            </div>
        </div>
    );
}

/* ---------- Personality strip (shared) ---------- */
function PersonalityStrip({ pet }) {
    const { t } = useI18n();
    const tags = (pet.personality || []).filter((k) => PERSONALITY_MAP[k]);
    if (tags.length === 0) return null;
    return (
        <section className="card-soft p-5 sm:p-6" data-testid="finder-personality">
            <div className="flex items-center gap-2 mb-3 text-paw-forest dark:text-paw-terra">
                <HeartStraight size={20} weight="fill" />
                <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("finder.personality_h", { name: pet.name })}</h2>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="finder-personality-list">
                {tags.map((k) => {
                    const meta = PERSONALITY_MAP[k];
                    return (
                        <span key={k}
                            data-testid={`finder-personality-${k}`}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold ${TONE_CLASS[meta.tone]}`}>
                            <span className="text-base leading-none">{meta.emoji}</span>
                            {t(meta.labelKey)}
                        </span>
                    );
                })}
            </div>
        </section>
    );
}

/* ---------- Passport documents (public only) ---------- */
function PassportPublicSection({ pet }) {
    const { t } = useI18n();
    const docs = pet.documents || [];
    if (docs.length === 0) return null;
    return (
        <section className="card-soft p-5 sm:p-6" data-testid="finder-passport">
            <div className="flex items-center gap-2 mb-3 text-paw-forest dark:text-paw-terra">
                <Certificate size={20} weight="fill" />
                <h2 className="font-display text-lg text-paw-ink dark:text-paw-bone">{t("finder.passport_h", { name: pet.name })}</h2>
            </div>
            <p className="text-xs text-paw-ink2 dark:text-paw-bone/60 mb-3">{t("finder.passport_sub")}</p>
            <ul className="space-y-2" data-testid="finder-passport-list">
                {docs.map((d) => {
                    const meta = DOC_KIND_META[d.kind || "other"] || DOC_KIND_META.other;
                    return (
                        <li key={d.id} className="flex items-center gap-3 rounded-2xl border border-paw-line dark:border-white/10 bg-white dark:bg-white/5 overflow-hidden"
                            data-testid={`finder-passport-doc-${d.id}`}>
                            <div className={`w-10 h-full min-h-[3rem] bg-gradient-to-br ${meta.tone} grid place-items-center text-white flex-shrink-0`}>
                                <span className="text-lg">{meta.emoji}</span>
                            </div>
                            <div className="flex-1 min-w-0 py-2">
                                <p className="text-sm font-semibold text-paw-ink dark:text-paw-bone truncate">{d.name}</p>
                                <p className="text-[11px] text-paw-ink2 dark:text-paw-bone/60">{t(meta.labelKey)}</p>
                            </div>
                            <a href={fileUrl(d.file_path)} target="_blank" rel="noreferrer"
                                data-testid={`finder-passport-view-${d.id}`}
                                className="mx-3 inline-flex items-center gap-1 text-xs font-semibold text-paw-forest dark:text-paw-terra hover:underline">
                                {t("finder.passport_view")} <ArrowSquareOut size={12} weight="bold" />
                            </a>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}

/* ---------- Found section (shared) ---------- */
function SightingShortcut({ publicId, petName }) {
    const { t } = useI18n();
    const [status, setStatus] = useState("idle"); // idle | locating | sending | sent | error
    const [err, setErr] = useState("");

    const sendSighting = () => {
        setStatus("locating"); setErr("");
        const submit = async (payload) => {
            try {
                await api.post(`/public/pets/${publicId}/sighting`, payload);
                setStatus("sent");
            } catch (_) {
                setErr(t("finder.sighting_error"));
                setStatus("error");
            }
        };
        if (!navigator.geolocation) { submit({}); return; }
        navigator.geolocation.getCurrentPosition(
            (pos) => submit({
                latitude: pos.coords.latitude,
                longitude: pos.coords.longitude,
                message: t("finder.sighting_default_msg", { name: petName }),
            }),
            () => submit({ message: t("finder.sighting_default_msg", { name: petName }) }),
            { timeout: 8000, enableHighAccuracy: true },
        );
    };

    if (status === "sent") {
        return (
            <section
                data-testid="finder-sighting-sent"
                className="rounded-3xl border border-emerald-500/25 bg-emerald-500/8 dark:bg-emerald-500/12 p-5 sm:p-6 text-center"
            >
                <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-500" />
                <h2 className="mt-3 font-display text-xl text-paw-ink dark:text-paw-bone">
                    {t("finder.sighting_sent_h")}
                </h2>
                <p className="mt-1 text-sm text-paw-ink2 dark:text-paw-bone/70">
                    {t("finder.sighting_sent_sub", { name: petName })}
                </p>
            </section>
        );
    }

    return (
        <section
            data-testid="finder-sighting"
            className="relative overflow-hidden rounded-3xl border border-amber-500/25 bg-gradient-to-br from-amber-500/8 via-amber-500/4 to-paw-terra/8 dark:from-amber-500/15 dark:via-amber-500/10 dark:to-paw-terra/15 p-5 sm:p-6"
        >
            <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-2xl bg-amber-500 text-white grid place-items-center flex-shrink-0 shadow-hover">
                    <NavigationArrow size={20} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                    <h2 className="font-display text-lg sm:text-xl text-paw-ink dark:text-paw-bone leading-tight">
                        {t("finder.sighting_h")}
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-paw-ink2 dark:text-paw-bone/70 leading-snug">
                        {t("finder.sighting_sub", { name: petName })}
                    </p>
                </div>
            </div>

            {err && (
                <div className="mt-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 px-4 py-2.5 text-xs">
                    {err}
                </div>
            )}

            <button
                type="button"
                onClick={sendSighting}
                disabled={status === "locating" || status === "sending"}
                data-testid="finder-sighting-send"
                aria-busy={status !== "idle" && status !== "error"}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-3 transition-all duration-300 hover:-translate-y-0.5 shadow-soft"
            >
                {status === "locating" ? (
                    <>
                        <span aria-hidden className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                        {t("finder.sighting_locating")}
                    </>
                ) : (
                    <>
                        <MapPin size={16} weight="fill" />
                        {t("finder.sighting_send")}
                    </>
                )}
            </button>
            <p className="mt-2 text-[10px] text-paw-ink2 dark:text-paw-bone/50 text-center">
                {t("finder.sighting_privacy")}
            </p>
        </section>
    );
}

/* ---------- Found section (shared) ---------- */
function VoiceMessageShortcut({ publicId, petName }) {
    const { t } = useI18n();
    const [audioFile, setAudioFile] = useState(null);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");

    const send = async () => {
        if (!audioFile || sending) return;
        setSending(true); setErr("");
        try {
            const fd = new FormData();
            fd.append("file", audioFile);
            const { data } = await api.post(`/public/upload/finder-audio`, fd);
            await api.post(`/public/pets/${publicId}/found`, {
                finder_audio_path: data.path,
                message: t("finder.voice_shortcut_default_msg"),
            });
            setSent(true);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || t("finder.voice_shortcut_error"));
        } finally { setSending(false); }
    };

    if (sent) {
        return (
            <section
                data-testid="finder-voice-shortcut-sent"
                className="rounded-3xl border border-emerald-500/25 bg-emerald-500/8 dark:bg-emerald-500/12 p-5 sm:p-6 text-center"
            >
                <CheckCircle size={36} weight="duotone" className="mx-auto text-emerald-500" />
                <h2 className="mt-3 font-display text-xl text-paw-ink dark:text-paw-bone">
                    {t("finder.voice_shortcut_sent_h")}
                </h2>
                <p className="mt-1 text-sm text-paw-ink2 dark:text-paw-bone/70">
                    {t("finder.voice_shortcut_sent_sub", { name: petName })}
                </p>
            </section>
        );
    }

    return (
        <section
            data-testid="finder-voice-shortcut"
            className="relative overflow-hidden rounded-3xl border border-paw-forest/20 dark:border-paw-terra/25 bg-gradient-to-br from-paw-forest/6 via-paw-forest/4 to-paw-terra/8 dark:from-paw-forest/18 dark:via-paw-forest/12 dark:to-paw-terra/15 p-5 sm:p-6"
        >
            <div aria-hidden className="absolute -top-16 -end-16 w-48 h-48 rounded-full bg-paw-terra/12 blur-3xl" />
            <div className="relative">
                <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-2xl bg-paw-terra text-white grid place-items-center flex-shrink-0 shadow-hover">
                        <Microphone size={20} weight="fill" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="font-display text-lg sm:text-xl text-paw-ink dark:text-paw-bone leading-tight">
                            {t("finder.voice_shortcut_h")}
                        </h2>
                        <p className="mt-1 text-xs sm:text-sm text-paw-ink2 dark:text-paw-bone/70 leading-snug">
                            {t("finder.voice_shortcut_sub", { name: petName })}
                        </p>
                    </div>
                </div>

                <div className="mt-4">
                    <AudioRecorder onFile={setAudioFile} />
                </div>

                {err && (
                    <div
                        data-testid="finder-voice-shortcut-error"
                        className="mt-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 text-red-700 dark:text-red-300 px-4 py-2.5 text-xs"
                    >
                        {err}
                    </div>
                )}

                <button
                    type="button"
                    onClick={send}
                    disabled={!audioFile || sending}
                    data-testid="finder-voice-shortcut-send"
                    aria-busy={sending}
                    className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-paw-forest hover:bg-paw-forestDark dark:bg-paw-terra dark:hover:bg-paw-terraDark disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold px-5 py-3 transition-all duration-300 hover:-translate-y-0.5 shadow-soft"
                >
                    {sending ? (
                        <>
                            <span aria-hidden className="w-4 h-4 rounded-full border-2 border-white/40 border-t-white animate-spin" />
                            {t("finder.voice_shortcut_sending")}
                        </>
                    ) : (
                        <>
                            <Microphone size={16} weight="fill" />
                            {t("finder.voice_shortcut_send")}
                        </>
                    )}
                </button>
            </div>
        </section>
    );
}

/* ---------- Found section (shared) ---------- */
function FoundSection({ pet, reported, openModal }) {
    const { t } = useI18n();
    return (
        <section className="card-soft p-5 sm:p-6 text-center" data-testid="finder-found-section">
            {!reported ? (
                <>
                    <HandHeart size={32} weight="duotone" className="mx-auto text-paw-terra" />
                    <h2 className="mt-3 font-display text-2xl text-paw-ink dark:text-paw-bone">
                        {t("finder.found_h", { name: pet.name })}
                    </h2>
                    <p className="mt-1 text-paw-ink2 dark:text-paw-bone/60">{t("finder.found_sub")}</p>
                    <button onClick={openModal} data-testid="finder-i-found-btn" className="mt-5 btn-secondary !py-4 !px-8 text-base">
                        <HandHeart size={18} weight="fill" />
                        <span className="ms-2">{t("finder.found_btn")}</span>
                    </button>
                </>
            ) : (
                <div className="py-6">
                    <CheckCircle size={40} weight="duotone" className="mx-auto text-paw-ok" />
                    <h2 data-testid="finder-thanks-h" className="mt-3 font-display text-2xl text-paw-ink dark:text-paw-bone">{t("finder.thanks_h")}</h2>
                    <p className="mt-1 text-paw-ink2 dark:text-paw-bone/60">
                        {t("finder.thanks_sub", { owner: pet.owner_name || t("finder.owner_generic") })}
                    </p>
                </div>
            )}
        </section>
    );
}

function FoundModal({ publicId, onClose, onSuccess }) {
    const { t } = useI18n();
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [location, setLocation] = useState("");
    const [msg, setMsg] = useState("");
    const [audioFile, setAudioFile] = useState(null);
    const [coords, setCoords] = useState(null);
    const [locStatus, setLocStatus] = useState("idle");
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState("");

    const shareLocation = () => {
        if (!navigator.geolocation) return;
        setLocStatus("sharing");
        navigator.geolocation.getCurrentPosition(
            (pos) => { setCoords({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }); setLocStatus("shared"); },
            () => setLocStatus("error"),
            { timeout: 8000, enableHighAccuracy: true }
        );
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true); setErr("");
        try {
            let finder_audio_path = null;
            if (audioFile) {
                const fd = new FormData();
                fd.append("file", audioFile);
                const { data } = await api.post(`/public/upload/finder-audio`, fd);
                finder_audio_path = data.path;
            }
            await api.post(`/public/pets/${publicId}/found`, {
                finder_name: name, finder_phone: phone, message: msg, location,
                latitude: coords?.latitude, longitude: coords?.longitude,
                finder_audio_path,
            });
            onSuccess();
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || "Could not submit");
        } finally { setSending(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-paw-ink/40 backdrop-blur-sm grid place-items-end sm:place-items-center px-4 pb-4 sm:pb-0" data-testid="found-modal">
            <form onSubmit={submit} className="card-soft w-full max-w-md p-6 space-y-3 overflow-y-auto max-h-[90vh]">
                <h3 className="font-display text-2xl text-paw-ink dark:text-paw-bone">{t("finder.modal_h")}</h3>
                <p className="text-sm text-paw-ink2 dark:text-paw-bone/60">{t("finder.modal_sub")}</p>
                <div>
                    <label className="label-soft">{t("finder.f_name")}</label>
                    <input className="input-soft mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="found-name" />
                </div>
                <div>
                    <label className="label-soft">{t("finder.f_phone")}</label>
                    <input className="input-soft mt-1.5" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="found-phone" />
                </div>
                <div>
                    <label className="label-soft">{t("finder.f_location")}</label>
                    <input className="input-soft mt-1.5" value={location} onChange={(e) => setLocation(e.target.value)}
                        placeholder={t("finder.f_location_ph")} data-testid="found-location" />
                    <button type="button" onClick={shareLocation}
                        className="mt-2 inline-flex items-center gap-1.5 text-xs text-paw-forest dark:text-paw-terra font-semibold hover:underline"
                        data-testid="found-share-loc">
                        <NavigationArrow size={14} weight="fill" />
                        {locStatus === "sharing" ? t("finder.location_sharing") : locStatus === "shared" ? `✓ ${t("finder.location_shared")}` : t("finder.share_location")}
                    </button>
                </div>
                <div>
                    <label className="label-soft">{t("finder.f_message")}</label>
                    <textarea rows={2} className="input-soft mt-1.5" value={msg} onChange={(e) => setMsg(e.target.value)} data-testid="found-message" />
                </div>
                <div>
                    <label className="label-soft">{t("finder.f_voice")}</label>
                    <div className="mt-1.5"><AudioRecorder onFile={setAudioFile} /></div>
                </div>
                {err && <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 text-sm">{err}</div>}
                <div className="flex gap-2 justify-end pt-2">
                    <button type="button" onClick={onClose} className="btn-outline text-sm" data-testid="found-cancel">{t("finder.cancel")}</button>
                    <button type="submit" disabled={sending} className="btn-secondary text-sm disabled:opacity-60" data-testid="found-submit">
                        {sending ? t("finder.sending") : t("finder.submit")}
                    </button>
                </div>
            </form>
        </div>
    );
}

function formatElapsed(ms, t) {
    if (!ms || ms < 0) return "";
    const s = Math.floor(ms / 1000);
    const m = Math.floor(s / 60), h = Math.floor(m / 60), d = Math.floor(h / 24);
    if (d >= 1) return t("safety.elapsed_d", { d });
    if (h >= 1) return t("safety.elapsed_h", { h });
    if (m >= 1) return t("safety.elapsed_m", { m });
    return t("safety.elapsed_now");
}

/* ---------- Small support helper shown below the "Powered by PawLink" line
 *            on the finder page. Only for finders who need technical help. */
function FinderSupportNote({ t }) {
    return (
        <p
            className="text-center text-[11px] text-paw-ink2/60 dark:text-paw-bone/40 pb-2"
            data-testid="finder-support-help"
        >
            {t("finder.tech_help")}{" "}
            <a
                href={supportMailto({ subject: "PawLink Support Request — Finder" })}
                className="text-paw-forest dark:text-paw-terra hover:underline font-medium"
                dir="ltr"
            >
                {SUPPORT_EMAIL}
            </a>
        </p>
    );
}
